import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {Edit} from '@material-ui/icons';
import {DatePicker} from '@material-ui/pickers';
import {
  InvalidTaxIdentificationNumber,
  TaxIdentificationNumber,
  ValidTaxIdentificationNumber,
} from 'Agent/Policies/models/TaxIdentificationNumber';
import BeneficiaryPercentage from 'Beneficiaries/components/BeneficiaryPercentage';
import {changeBeneficiaryDetails, fetchBeneficiaryDetails} from 'Beneficiaries/ducks/beneficiaryDetail';
import {
  changeBeneficiaryPerson,
  changePolicyBeneficiaryPersons,
  clearState,
  updateField,
  validateAndSaveBeneficiaryPercents,
  validateAndSubmitForm,
} from 'Beneficiaries/ducks/beneficiaryForm';
import {
  BeneficiaryPerson,
  EmptyPersonType,
  InvalidBeneficiaryPerson,
  InvalidBeneficiaryPersonDateOfBirth,
  ValidBeneficiaryPerson,
  ValidBeneficiaryPersonDateOfBirth,
  ValidPersonType,
} from 'Beneficiaries/models/BeneficiaryPerson';
import {ContingentBeneficiary, PersonTypes, PrimaryBeneficiary} from 'Beneficiaries/models/PersonTypes';
import {BeneficiariesRouteConstants} from 'Beneficiaries/Routes';
import {Empty, isLoaded, isLoading, Loaded} from 'Core/models/Async';
import {InvalidEmail, ValidEmail} from 'Core/models/Email';
import {EmptyString, StringUnion} from 'Core/models/EmptyString';
import {InvalidPhoneNumber, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {Eq} from 'fp-ts/lib/Eq';
import qs from 'query-string';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import {maskPhoneNumber, unmaskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';
import {maskTaxId, unMaskTaxId} from 'Shared/utilities/taxIdMasker';
import './BeneficiaryForm.sass';
import { getManagedStatus } from 'ApplicationEntry/api/awdApi';
import { Alert } from '@material-ui/lab';

const BeneficiaryForm = () => {
  const dispatch = useDispatch();
  const {search} = useLocation();
  const id = qs.parse(search).id as string;
  const {policyNumber} = useParams();
  const history = useHistory();
  const {beneficiaryDetails} = useSelector((state: ReducerState) => state.beneficiaryDetail);
  const {beneficiaries} = useSelector((state: ReducerState) => state.beneficiaries);
  const [sigDisabled, setSigDisabled] = useState(false);
  const {
    // The current person to be edit/add
    beneficiaryPerson,
    // Its state upon page load
    originalBeneficiaryPerson,
    // all other persons with percentage info in the same policy
    policyBeneficiaryPersons,
    originalPolicyBeneficiaryPersons,
    primaryBeneficiaryPercentError,
    contingentBeneficiaryPercentError,
  } = useSelector((state: ReducerState) => state.beneficiaryForm);

  useEffect(() => {
    if (policyNumber && id) {
      dispatch(fetchBeneficiaryDetails(policyNumber));
    } else if (!policyNumber) {
      history.push(BeneficiariesRouteConstants.BENEFICIARIES);
    } else {
      dispatch(changeBeneficiaryPerson(new Loaded({})));
    }

    return () => {
      dispatch(clearState());
      dispatch(changeBeneficiaryDetails(new Empty()));
    };
  }, [policyNumber, id]);

  const fetchUserDataforBene = async (caseNumber: any) => {
    const user1 = await getManagedStatus(caseNumber);
    if (user1._tag === "Right") {
      let pacDataResultData2 = user1['right']['beneficiaryCaseDetail']['managedStatus'];
      let emp;
      if (pacDataResultData2 === null) {
        emp = "AHL Managed";
      } else {
        emp = pacDataResultData2;
      }
      if (emp === "Employer Managed") {
        setSigDisabled(true);
      } else {
        setSigDisabled(false);
      }
    } else {
      setSigDisabled(false);
    }
  }

  useEffect(() => {
    let workQueueData = (beneficiaries.key === 'Loaded' ? beneficiaries.Data.result : null);
    const BFD = workQueueData?.find(item => item.policyNumber === policyNumber);
    const CN = BFD?.caseNumber;
    if (isLoaded(beneficiaryDetails)) {
      if (beneficiaryDetails.Data.length > 0 && id) {
        const currentPerson = beneficiaryDetails.Data.find(bd => bd.id === id);
        if (currentPerson) {
          fetchUserDataforBene(CN);
          dispatch(changeBeneficiaryPerson(new Loaded(currentPerson)));
        } else {
          history.push(BeneficiariesRouteConstants.BENEFICIARY_DETAIL(policyNumber!));
        }
      } else {
        dispatch(changeBeneficiaryPerson(new Loaded({})));
      }

      dispatch(changePolicyBeneficiaryPersons(beneficiaryDetails));
    }
  }, [beneficiaryDetails, id]);

  const equalIdentifier: Eq<TaxIdentificationNumber | StringUnion | undefined> = {
    equals: (x, y) => x?.value === y?.value,
  };

  const shouldDisableIdentifierField = (identifier?: TaxIdentificationNumber | StringUnion) =>
    equalIdentifier.equals(identifier, (originalBeneficiaryPerson as Loaded<BeneficiaryPerson>).Data.identifier) &&
    !(identifier instanceof EmptyString) &&
    !!id;

  const equalBeneficiaryPerson: Eq<BeneficiaryPerson> = {
    equals: (x, y) =>
      x.identifier?.value === y.identifier?.value &&
      x.firstName === y.firstName &&
      x.middleName === y.middleName &&
      x.lastName === y.lastName &&
      x.gender === y.gender &&
      x.phoneNumber?.phoneNumber === y.phoneNumber?.phoneNumber &&
      x.personType === y.personType &&
      x.dateOfBirth?.value === y.dateOfBirth?.value &&
      x.email?.address === y.email?.address &&
      x.relationship === y.relationship &&
      x.address1 === y.address1 &&
      x.address2 === y.address2 &&
      x.address3 === y.address3 &&
      x.city === y.city &&
      x.state === y.state &&
      x.zipcode === y.zipcode &&
      x.signatureDate === y.signatureDate,
  };

  const equalBeneficiaryPercentage: Eq<BeneficiaryPerson[]> = {
    equals: (x, y) => {
      return x.every(bpx => y.find(bpy => bpy.id === bpx.id)?.beneficiaryPercent === bpx.beneficiaryPercent);
    },
  };

  const fieldIsRequiredErrorMessage = 'Field is Required';
  const isInsert = !id;

  // If person is of type beneficiary, then allow add or delete, as well as all editing
  const isBeneficiary = (personTypeId?: number) =>
    personTypeId === PrimaryBeneficiary.id || personTypeId === ContingentBeneficiary.id;

  // If user is not inserting new record and the selected person type is not a beneficiary, then only allow editing on phone number and addresses.
  // This is due to L70 only have info for phone number and address, we need to be consistant.
  const onlyAllowPhoneAndAddressEditing =
    !isInsert && isLoaded(beneficiaryPerson) && !isBeneficiary(beneficiaryPerson.Data?.personType?.id);

  return (
    <Grid className="beneficiary_form" container alignItems="center" direction="column">
      <Paper className="beneficiary_form__paper">
        <Typography variant="h6" className="beneficiary_form__title">{`${id ? 'Update' : 'Add'} Person`}</Typography>
        {isLoaded(beneficiaryPerson) ? (
          <form className="beneficiary_form__form" noValidate>
            {sigDisabled.valueOf().toString() === 'true' ? (
            <Alert severity="info" className="case-details-section__reserved-alert">
              Beneficiary is Employer Managed.
            </Alert>
          ) : null}
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth variant="outlined" label="Policy Number" disabled={true} value={policyNumber} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel
                    htmlFor="identifier-input"
                    error={beneficiaryPerson.Data?.identifier instanceof InvalidTaxIdentificationNumber}
                  >
                    SSN
                  </InputLabel>
                  <OutlinedInput
                    id="identifier-input"
                    fullWidth
                    placeholder="XXX-XX-XXXX"
                    labelWidth={35}
                    inputProps={{
                      maxLength: 11,
                    }}
                    disabled={
                      shouldDisableIdentifierField(beneficiaryPerson.Data.identifier) || onlyAllowPhoneAndAddressEditing 
                    }
                    value={maskTaxId(beneficiaryPerson.Data?.identifier?.value) || ''}
                    endAdornment={
                      shouldDisableIdentifierField(beneficiaryPerson.Data.identifier) &&
                      !onlyAllowPhoneAndAddressEditing && (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="editing SSN"
                            onClick={() => dispatch(updateField({identifier: ValidTaxIdentificationNumber.create('')}))}
                            edge="end"
                          >
                            <Edit color="primary" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                    onChange={e =>
                      dispatch(
                        updateField({identifier: ValidTaxIdentificationNumber.create(unMaskTaxId(e.target.value))})
                      )
                    }
                    error={beneficiaryPerson.Data?.identifier instanceof InvalidTaxIdentificationNumber}
                  />
                  {beneficiaryPerson.Data?.identifier instanceof InvalidTaxIdentificationNumber && (
                    <FormHelperText id="identifer-helper-text" error>
                      {beneficiaryPerson.Data?.identifier.error}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="First Name"
                  value={beneficiaryPerson.Data?.firstName || ''}
                  onChange={e => dispatch(updateField({firstName: e.target.value}))}
                  disabled={onlyAllowPhoneAndAddressEditing || sigDisabled}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Middle Name"
                  value={beneficiaryPerson.Data?.middleName || ''}
                  onChange={e => dispatch(updateField({middleName: e.target.value}))}
                  disabled={onlyAllowPhoneAndAddressEditing || sigDisabled}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Last Name"
                  value={beneficiaryPerson.Data?.lastName || ''}
                  onChange={e => dispatch(updateField({lastName: e.target.value}))}
                  disabled={onlyAllowPhoneAndAddressEditing || sigDisabled}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Relationship"
                  value={beneficiaryPerson.Data?.relationship || ''}
                  onChange={e => dispatch(updateField({relationship: e.target.value}))}
                  disabled={onlyAllowPhoneAndAddressEditing || sigDisabled}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  fullWidth
                  variant="inline"
                  inputVariant="outlined"
                  color="primary"
                  label="Date of Birth"
                  format="MM/dd/yyyy"
                  maxDate={new Date()}
                  value={beneficiaryPerson.Data?.dateOfBirth?.value || null}
                  onChange={date =>
                    dispatch(
                      updateField({
                        dateOfBirth: ValidBeneficiaryPersonDateOfBirth.create(
                          date ? toFormattedDate(date.toString()) : undefined
                        ),
                      })
                    )
                  }
                  disabled={onlyAllowPhoneAndAddressEditing || sigDisabled}
                  error={beneficiaryPerson.Data?.dateOfBirth instanceof InvalidBeneficiaryPersonDateOfBirth}
                  helperText={
                    beneficiaryPerson.Data?.dateOfBirth instanceof InvalidBeneficiaryPersonDateOfBirth &&
                    beneficiaryPerson.Data?.dateOfBirth.validationError
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="(XXX)-XXX XXXX"
                  inputProps={{
                    maxLength: 14,
                  }}
                  variant="outlined"
                  label="Phone Number"
                  value={maskPhoneNumber(beneficiaryPerson.Data?.phoneNumber?.phoneNumber) || ''}
                  onChange={e =>
                    dispatch(updateField({phoneNumber: ValidPhoneNumber.create(unmaskPhoneNumber(e.target.value))}))
                  }
                  error={beneficiaryPerson.Data?.phoneNumber instanceof InvalidPhoneNumber}
                  helperText={
                    beneficiaryPerson.Data?.phoneNumber instanceof InvalidPhoneNumber &&
                    beneficiaryPerson.Data?.phoneNumber.validationError
                  }
                  disabled={sigDisabled} 
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Gender"
                  value={beneficiaryPerson.Data?.gender || ''}
                  onChange={e => dispatch(updateField({gender: e.target.value}))}
                  disabled={onlyAllowPhoneAndAddressEditing || sigDisabled}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl required variant="outlined" fullWidth>
                  <InputLabel id="person-type-label">Person Type</InputLabel>
                  <Select
                    labelId="person-type-label"
                    value={beneficiaryPerson.Data?.personType?.id || ''}
                    onChange={e =>
                      dispatch(updateField({personType: ValidPersonType.create(e.target.value as number)}))
                    }
                    disabled={(!isBeneficiary(beneficiaryPerson.Data?.personType?.id) && !isInsert) || sigDisabled}
                    labelWidth={105}
                    required
                    error={beneficiaryPerson.Data?.personType instanceof EmptyPersonType}
                  >
                    {PersonTypes.map(personType => (
                      <MenuItem key={personType.id} value={personType.id} disabled={!isBeneficiary(personType.id) || sigDisabled}>
                        {personType.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {beneficiaryPerson.Data?.personType instanceof EmptyPersonType && (
                    <FormHelperText error>{fieldIsRequiredErrorMessage}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Email"
                  value={beneficiaryPerson.Data?.email?.address || ''}
                  onChange={e => dispatch(updateField({email: ValidEmail.create(e.target.value)}))}
                  disabled={onlyAllowPhoneAndAddressEditing || sigDisabled}
                  error={beneficiaryPerson.Data?.email instanceof InvalidEmail}
                  helperText={
                    beneficiaryPerson.Data?.email instanceof InvalidEmail &&
                    beneficiaryPerson.Data?.email.validationError
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Address 1"
                  value={beneficiaryPerson.Data?.address1 || ''}
                  onChange={e => dispatch(updateField({address1: e.target.value}))}
                  disabled={sigDisabled} 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Address 2"
                  value={beneficiaryPerson.Data?.address2 || ''}
                  onChange={e => dispatch(updateField({address2: e.target.value}))}
                  disabled={sigDisabled} 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Address 3"
                  value={beneficiaryPerson.Data?.address3 || ''}
                  onChange={e => dispatch(updateField({address3: e.target.value}))}
                  disabled={sigDisabled} 
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="City"
                  value={beneficiaryPerson.Data?.city || ''}
                  onChange={e => dispatch(updateField({city: e.target.value}))}
                  disabled={sigDisabled} 
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="State"
                  value={beneficiaryPerson.Data?.state || ''}
                  onChange={e => dispatch(updateField({state: e.target.value}))}
                  disabled={sigDisabled} 
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Zip Code"
                  value={beneficiaryPerson.Data?.zipcode || ''}
                  onChange={e => dispatch(updateField({zipcode: e.target.value}))}
                  disabled={sigDisabled} 
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  fullWidth
                  variant="inline"
                  inputVariant="outlined"
                  color="primary"
                  label="Signature Date"
                  format="MM/dd/yyyy"
                  // maxDate={new Date()}
                  value={`${beneficiaryPerson.Data?.signatureDate || '01-01-1900'}`}
                  onChange={date =>
                    dispatch(
                      updateField({
                        signatureDate: date?.toJSDate() || beneficiaryPerson.Data?.signatureDate
                      })
                    )
                  }
                  disabled={sigDisabled} 
                />
              </Grid>
              <Grid item container xs={12} justify="flex-end">
                <Button
                  color="primary"
                  onClick={() => history.push(BeneficiariesRouteConstants.BENEFICIARY_DETAIL(policyNumber!))}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={
                    equalBeneficiaryPerson.equals(
                      beneficiaryPerson.Data,
                      (originalBeneficiaryPerson as Loaded<BeneficiaryPerson>).Data
                    ) || ValidBeneficiaryPerson.create(beneficiaryPerson.Data) instanceof InvalidBeneficiaryPerson
                  }
                  onClick={() => dispatch(validateAndSubmitForm(beneficiaryPerson.Data, policyNumber!))}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </form>
        ) : (
          <div className="beneficiary_form__loading-container">
            <CircularProgress color="primary" />
          </div>
        )}

        <Grid item container xs={12} justify="center">
          {isLoading(policyBeneficiaryPersons) && id ? (
            <Grid item>
              <CircularProgress color="primary" />
            </Grid>
          ) : (
            isLoaded(policyBeneficiaryPersons) &&
            policyBeneficiaryPersons.Data.filter(
              bp => bp.personType?.id === PrimaryBeneficiary.id || bp.personType?.id === ContingentBeneficiary.id
            ).length > 0 && (
              <Grid item container spacing={4}>
                <BeneficiaryPercentage
                  beneficiaries={policyBeneficiaryPersons.Data.filter(
                    bp => bp.personType?.id === PrimaryBeneficiary.id
                  )}
                  title="Primary Beneficiaries"
                  hasError={primaryBeneficiaryPercentError}
                  disabled={!id || sigDisabled}
                />
                <BeneficiaryPercentage
                  beneficiaries={policyBeneficiaryPersons.Data.filter(
                    bp => bp.personType?.id === ContingentBeneficiary.id
                  )}
                  title="Contingent Beneficiaries"
                  hasError={contingentBeneficiaryPercentError}
                  disabled={!id || sigDisabled}
                />
                <Grid item container xs={12} justify="flex-end">
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={
                      primaryBeneficiaryPercentError ||
                      contingentBeneficiaryPercentError ||
                      equalBeneficiaryPercentage.equals(
                        policyBeneficiaryPersons.Data,
                        (originalPolicyBeneficiaryPersons as Loaded<BeneficiaryPerson[]>).Data
                      )
                    }
                    onClick={() =>
                      dispatch(validateAndSaveBeneficiaryPercents(policyNumber!, policyBeneficiaryPersons.Data, id!))
                    }
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            )
          )}
        </Grid>
      </Paper>
    </Grid>
  );
};

export default BeneficiaryForm;
